export interface DirectUploadRequest {
  accessToken?: string;
  extraFields?: Record<string, string>;
  file: File;
  // Share of the file sent to storage (0..1). When given, the storage POST goes through XHR, the
  // only browser API that reports upload progress.
  onUploadProgress?: (share: number) => void;
  // REST route that exposes `/upload-url` and `/complete`, e.g. `${API}/services/:uuid/media`.
  routeUrl: string;
}

// An API refusal of `upload-url` or `complete`; `code` is the API's `errorCode` (or `code`), for
// clients to localize.
export class DirectUploadError extends Error {
  readonly code?: string;
  readonly status?: number;

  constructor(message: string, options: { code?: string; status?: number } = {}) {
    super(message);
    this.name = 'DirectUploadError';
    this.code = options.code;
    this.status = options.status;
    Object.setPrototypeOf(this, DirectUploadError.prototype);
  }
}

interface DirectUploadTarget {
  // Form fields of a presigned POST; S3 checks the file size against their policy.
  fields?: Record<string, string>;
  headers: Record<string, string>;
  method: 'POST' | 'PUT';
  s3Key: string;
  uploadUrl: string;
}

function buildStorageUploadForm(fields: Record<string, string>, file: File) {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    form.append(name, value);
  }
  // S3 ignores every field after `file`, so it goes last.
  form.append('file', file);
  return form;
}

export async function uploadFileDirectly<TResult>({
  accessToken,
  extraFields = {},
  file,
  onUploadProgress,
  routeUrl,
}: DirectUploadRequest): Promise<TResult> {
  const fileName = file.name;
  const mimeType = file.type || 'application/octet-stream';

  const uploadTarget = await postJson<DirectUploadTarget>(
    `${routeUrl}/upload-url`,
    { ...extraFields, fileName, mimeType, uploadProtocol: 'post' },
    accessToken
  );

  let storageResponse: { ok: boolean; status: number };
  try {
    storageResponse =
      uploadTarget.method === 'POST' && onUploadProgress
        ? await postFormWithProgress(
            uploadTarget.uploadUrl,
            buildStorageUploadForm(uploadTarget.fields ?? {}, file),
            onUploadProgress
          )
        : uploadTarget.method === 'POST'
        ? await fetch(uploadTarget.uploadUrl, {
            method: 'POST',
            mode: 'cors',
            body: buildStorageUploadForm(uploadTarget.fields ?? {}, file),
          })
        : await fetch(uploadTarget.uploadUrl, {
            method: uploadTarget.method,
            mode: 'cors',
            headers: uploadTarget.headers,
            body: file,
          });
  } catch (error) {
    console.error('[direct-upload] Storage upload network request failed.', error);
    throw new Error('Unable to upload the file to storage.');
  }

  if (!storageResponse.ok) {
    throw new Error(`File upload failed with status ${storageResponse.status}.`);
  }
  onUploadProgress?.(1);

  return postJson<TResult>(
    `${routeUrl}/complete`,
    { ...extraFields, s3Key: uploadTarget.s3Key, fileName, mimeType },
    accessToken
  );
}

function postFormWithProgress(
  url: string,
  form: FormData,
  onProgress: (share: number) => void
): Promise<{ ok: boolean; status: number }> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', url);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(1, event.loaded / event.total));
      }
    };
    request.onload = () =>
      resolve({ ok: request.status >= 200 && request.status < 300, status: request.status });
    request.onerror = () => reject(new Error('Storage upload network request failed.'));
    request.send(form);
  });
}

async function postJson<T>(
  url: string,
  body: Record<string, unknown>,
  accessToken?: string
): Promise<T> {
  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    console.error('[direct-upload] API request failed.', error);
    throw new Error('Unable to reach the API. Ensure the API server is running.');
  }

  let responseBody: unknown;
  try {
    responseBody = (await response.json()) as unknown;
  } catch {
    responseBody = null;
  }

  if (!response.ok) {
    console.error('[direct-upload] API request was rejected.', {
      status: response.status,
      responseBody,
    });
    throw new DirectUploadError(
      extractHttpErrorMessage(responseBody) ||
        `Upload request failed with status ${response.status}.`,
      { code: extractHttpErrorCode(responseBody), status: response.status }
    );
  }

  if (!responseBody || typeof responseBody !== 'object') {
    throw new Error('Upload response did not include data.');
  }

  return responseBody as T;
}

function extractHttpErrorMessage(responseBody: unknown): string | null {
  if (!responseBody || typeof responseBody !== 'object') {
    return null;
  }

  const { message } = responseBody as { message?: string | string[] };

  if (Array.isArray(message)) {
    return message.map((item) => item.trim()).filter(Boolean).join(' ') || null;
  }

  return typeof message === 'string' ? message.trim() || null : null;
}

function extractHttpErrorCode(responseBody: unknown): string | undefined {
  if (!responseBody || typeof responseBody !== 'object') {
    return undefined;
  }

  const { errorCode, code } = responseBody as { errorCode?: unknown; code?: unknown };
  const value = [errorCode, code].find((item) => typeof item === 'string' && item.trim());
  return typeof value === 'string' ? value.trim() : undefined;
}
